/**
 * 日付入力欄（DateField / 独自 DatePicker）用の解析・書式・カレンダー計算。
 *
 * - 値（API・フォームの値）は ISO 形式：日付 `YYYY-MM-DD`、年月 `YYYY-MM`。
 * - 画面表示は `YYYY/MM/DD`（年月は `YYYY/MM`）。
 * - 手入力は西暦（区切り `/` `-` `.`・区切りなし 8 桁・`2026年10月21日`）と和暦（`R8.10.21`・`令和8年10月21日`）を受け付ける。
 *   全角数字・全角記号も可（NFKC 正規化）。
 */
import { formatISO, fromWareki, isValidYMD, toWareki, weekdayJa, type YMD } from './wareki';

export type DateKind = 'date' | 'month';

export type DateParseResult = { status: 'empty' } | { status: 'ok'; value: string } | { status: 'invalid' };

const MIN_YEAR = 1000;
const MAX_YEAR = 9999;

function normalizeText(text: string): string {
  return text.normalize('NFKC').replace(/\s+/g, '').toUpperCase();
}

function okYMD(ymd: YMD): string | null {
  if (ymd.y < MIN_YEAR || ymd.y > MAX_YEAR) return null;
  return isValidYMD(ymd) ? formatISO(ymd) : null;
}

function okYM(y: number, m: number): string | null {
  if (y < MIN_YEAR || y > MAX_YEAR || m < 1 || m > 12) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}`;
}

const DATE_SEP_RE = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/;
const DATE_COMPACT_RE = /^(\d{4})(\d{2})(\d{2})$/;
const DATE_KANJI_RE = /^(\d{4})年(\d{1,2})月(\d{1,2})日?$/;
const MONTH_SEP_RE = /^(\d{4})[-/.](\d{1,2})$/;
const MONTH_COMPACT_RE = /^(\d{4})(\d{2})$/;
const MONTH_KANJI_RE = /^(\d{4})年(\d{1,2})月?$/;
const MONTH_WAREKI_RE = /^(明治|大正|昭和|平成|令和|[MTSHR])(元|\d{1,2})[年.\-/](\d{1,2})月?$/;

/**
 * 手入力の文字列を解釈する。
 * @example parseDateText('2026/1/5') // => { status: 'ok', value: '2026-01-05' }
 * @example parseDateText('R8.10.21') // => { status: 'ok', value: '2026-10-21' }
 * @example parseDateText('2026/02/30') // => { status: 'invalid' }
 * @example parseDateText('2026/10', 'month') // => { status: 'ok', value: '2026-10' }
 */
export function parseDateText(text: string, kind: DateKind = 'date'): DateParseResult {
  const t = normalizeText(text);
  if (t === '') return { status: 'empty' };
  let value: string | null = null;
  if (kind === 'date') {
    const m = DATE_SEP_RE.exec(t) ?? DATE_COMPACT_RE.exec(t) ?? DATE_KANJI_RE.exec(t);
    if (m) value = okYMD({ y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) });
    else value = fromWareki(t);
  } else {
    const m = MONTH_SEP_RE.exec(t) ?? MONTH_COMPACT_RE.exec(t) ?? MONTH_KANJI_RE.exec(t);
    if (m) value = okYM(Number(m[1]), Number(m[2]));
    else {
      const w = MONTH_WAREKI_RE.exec(t);
      const iso = w ? fromWareki(`${w[1]}${w[2]}年${w[3]}月1日`) : null;
      value = iso ? iso.slice(0, 7) : null;
    }
  }
  return value ? { status: 'ok', value } : { status: 'invalid' };
}

/**
 * 入力途中で即時確定してよい「桁のそろった」形式か（`2026/10/21` `2026-10-21` `20261021`、年月は `2026/10` `202610`）。
 * `2026/10/2` のような入力途中の値で確定（＝onChange）が走らないようにするための判定。
 */
export function isCompleteDateText(text: string, kind: DateKind = 'date'): boolean {
  const t = normalizeText(text);
  return kind === 'date' ? /^\d{4}[-/.]\d{2}[-/.]\d{2}$|^\d{8}$/.test(t) : /^\d{4}[-/.]\d{2}$|^\d{6}$/.test(t);
}

/** ISO 値か（`YYYY-MM-DD` / `YYYY-MM`、実在する日付） */
export function isIsoValue(value: string, kind: DateKind = 'date'): boolean {
  if (kind === 'date') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return !!m && okYMD({ y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) }) !== null;
  }
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  return !!m && okYM(Number(m[1]), Number(m[2])) !== null;
}

/** ISO 値 → 表示文字列（`2026/10/21`・`2026/10`）。ISO でなければそのまま返す */
export function formatDateText(value: string | null | undefined, kind: DateKind = 'date'): string {
  if (!value) return '';
  if (!isIsoValue(value, kind)) return value;
  return value.replace(/-/g, '/');
}

/** min / max（ISO）の範囲内か。値・範囲とも同じ桁の ISO 文字列なので文字列比較でよい */
export function isWithinRange(value: string, min?: string, max?: string): boolean {
  if (min && value < min) return false;
  if (max && value > max) return false;
  return true;
}

// ---------------------------------------------------------------------------
// カレンダー計算（ローカル日付のみ。タイムゾーンの影響を受けない）
// ---------------------------------------------------------------------------

export function splitISO(iso: string): YMD {
  const [y = '1970', m = '1', d = '1'] = iso.split('-');
  return { y: Number(y), m: Number(m), d: Number(d) };
}

export function daysInMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

/** n 日後（負数で前） */
export function addDaysISO(iso: string, n: number): string {
  const { y, m, d } = splitISO(iso);
  const dt = new Date(y, m - 1, d + n);
  return formatISO({ y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() });
}

/** n か月後。日は月末で丸める（1/31 → 2/28） */
export function addMonthsISO(iso: string, n: number): string {
  const { y, m, d } = splitISO(iso);
  const idx = y * 12 + (m - 1) + n;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return formatISO({ y: ny, m: nm, d: Math.min(d, daysInMonth(ny, nm)) });
}

/** その月の 1 日を含む週の日曜から 6 週（42 日）分の日付 */
export function monthMatrix(y: number, m: number): string[] {
  const first = new Date(y, m - 1, 1);
  const start = new Date(y, m - 1, 1 - first.getDay());
  const out: string[] = [];
  for (let i = 0; i < 42; i++) {
    const dt = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    out.push(formatISO({ y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() }));
  }
  return out;
}

/** 曜日番号（0=日〜6=土） */
export function dayOfWeek(iso: string): number {
  const { y, m, d } = splitISO(iso);
  return new Date(y, m - 1, d).getDay();
}

/** 見出し用の和暦年（その月 1 日時点の元号。2019-05 → 令和元年、2019-04 → 平成31年） */
export function warekiYearOfMonth(y: number, m: number): string {
  return toWareki(formatISO({ y, m, d: 1 }), { style: 'year' });
}

/** 見出し用の和暦年（その年の 12/31 時点の元号。改元年は新元号） */
export function warekiYearOfYear(y: number): string {
  return toWareki(formatISO({ y, m: 12, d: 31 }), { style: 'year' });
}

/** カレンダーの年月見出し（例「令和8年（2026年）10月」） */
export function calendarHeading(y: number, m: number): string {
  const w = warekiYearOfMonth(y, m);
  return w ? `${w}（${y}年）${m}月` : `${y}年${m}月`;
}

/** 日セルの読み上げ名（例「2026年10月21日（水）」） */
export function dayLabel(iso: string): string {
  const { y, m, d } = splitISO(iso);
  return `${y}年${m}月${d}日（${weekdayJa(iso)}）`;
}
